import type { DiscoverEntitiesResponse, EntityDiscoveryStatusResponse } from '../../api/types';

interface DiscoveryService {
  discoverEntities: (id: string, forceRefresh?: boolean) => Promise<DiscoverEntitiesResponse>;
  getDiscoveryStatus: (id: string) => Promise<EntityDiscoveryStatusResponse>;
}

function waitForPoll(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    signal.throwIfAborted();
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, 1500);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Bypass both discovery caches; serialize and bound polling without changing selections. */
export async function refreshEntityDiscovery(
  connectionId: string, service: DiscoveryService, signal: AbortSignal,
  wait: (signal: AbortSignal) => Promise<void> = waitForPoll,
): Promise<NonNullable<EntityDiscoveryStatusResponse['result']>> {
  signal.throwIfAborted();
  await service.discoverEntities(connectionId, true);
  for (let attempt = 0; attempt < 120; attempt++) {
    signal.throwIfAborted();
    await wait(signal);
    signal.throwIfAborted();
    const status = await service.getDiscoveryStatus(connectionId);
    signal.throwIfAborted();
    if (status.status === 'error') throw new Error(status.error || 'Discovery refresh failed');
    if (status.status === 'complete') {
      if (!status.result) throw new Error('Discovery refresh completed without metadata');
      return status.result;
    }
  }
  throw new Error('Discovery refresh timed out. Retry refreshing discovery.');
}